import { Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import {
  AgentNode,
  CreateAgentSubstitutionGQL,
  CreateAgentSubstitutionMutationVariables,
  EndAgentSubstitutionGQL,
  GetActiveAgentSubstitutionGQL,
  GetActiveAgentSubstitutionQuery,
  GetAvailableAgentsForSubstitutionGQL,
  GetAvailableAgentsForSubstitutionQuery,
} from '@generated/graphql';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export type AgentSubstitution = NonNullable<
  GetActiveAgentSubstitutionQuery['activeAgentSubstitution']
>;

@Injectable({
  providedIn: 'root',
})
export class AgentSubstitutionService {
  constructor(
    private getActiveAgentSubstitutionGQL: GetActiveAgentSubstitutionGQL,
    private getAvailableAgentsForSubstitutionGQL: GetAvailableAgentsForSubstitutionGQL,
    private createAgentSubstitutionGQL: CreateAgentSubstitutionGQL,
    private endAgentSubstitutionGQL: EndAgentSubstitutionGQL,
  ) {}

  getAvailableAgents(): Observable<AgentNode[]> {
    return this.getAvailableAgentsForSubstitutionGQL
      .fetch({}, { fetchPolicy: 'cache-first' })
      .pipe(map(mapAvailableAgentsForSubstitution));
  }

  getActive(): Observable<AgentSubstitution | null> {
    return this.getActiveAgentSubstitutionGQL
      .fetch({}, { fetchPolicy: 'network-only' })
      .pipe(map(mapActiveSubstitution));
  }

  create(variables: CreateAgentSubstitutionMutationVariables) {
    return this.createAgentSubstitutionGQL
      .mutate(variables)
      .pipe(map(({ data }) => data?.createAgentSubstitution?.result as AgentSubstitution));
  }

  end(substitutionId: string) {
    return this.endAgentSubstitutionGQL.mutate({ substitutionId });
  }
}

const mapActiveSubstitution = ({ data }: ApolloQueryResult<GetActiveAgentSubstitutionQuery>) =>
  data?.activeAgentSubstitution ?? null;

const mapAvailableAgentsForSubstitution = ({
  data,
}: ApolloQueryResult<GetAvailableAgentsForSubstitutionQuery>) =>
  data?.userAuth?.availableAgentsForSubstitution ?? [];

export function toSubstitutionDateTime(isoDate: string): string {
  return `${isoDate}T00:00:00.000Z`;
}

// The substitution window is [start_at, end_at) — end_at is EXCLUSIVE (open_substitution_criterion
// keeps a row open only while end_at > now). A picked end DATE therefore means "through that day",
// which is the next day's midnight, not the picked day's own: encoding `2026-10-10` as
// `2026-10-10T00:00:00Z` ended the closure at the START of the 10th and left the 10th itself
// uncovered (and end == start for a single-day closure, which the backend refuses).
export function toSubstitutionEndDateTime(isoDate: string): string {
  const end = new Date(toSubstitutionDateTime(isoDate));
  end.setUTCDate(end.getUTCDate() + 1);
  return end.toISOString();
}
